/**
 *
 *  Header
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 23/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, NavLink } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { useAppState } from "../../../state";
import { useRegion } from "../../../context/RegionProvider";

// Components
import Button from "../../Button/Button";
import Menu from "../../Header/Menu/Menu";

// Utils
import { clientWidth } from "../../../utils";

// Icons
import UserPicOIcon from "../../../icons/UserPicOIcon";

const Header = ({ isVisible, isNight, showLinks, showMenu }) => {
  const { user } = useAppState();
  const { region } = useRegion();

  const [menuOpen, setMenuOpen] = useState(false);

  const isLoggedIn = !!user;

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <>
      {menuOpen && <div />}
      <nav
        role="navigation"
        className={`nav nav-top ${
          menuOpen ? "tbk-fixed tbk-z-995" : ""
        } tbk-h-7 tbk-w-full tbk-border-b tbk-border-solid tbk-border-blue-grey-light ${
          isNight ? " inverse" : ""
        }${isVisible ? " is-show" : ""} tbk-bg-basic-white`}
      >
        <div className="tbk-container tbk-mx-auto tbk-h-full tbk-px-2">
          <div className="tbk-relative tbk-flex tbk-h-full tbk-items-center tbk-py-1">
            <div className="tbk-mr-0 md:tbk-mr-7">
              <div className="tb-logo tbk-ml-0 tbk-max-w-[165px]">
                <a
                  className="tb-logo-link tbk-text-grey-main"
                  href={`/${region === "US" ? "us" : ""}?logo=1`}
                >
                  <svg
                    className="trube-logo-icon tbk-block tbk-w-28 tbk-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    width="165"
                    height="48"
                    viewBox="0 0 825.144 253.659"
                    role="presentation"
                    aria-hidden="true"
                  >
                    <defs>
                      <path
                        id="trube-logo-header-1"
                        d="M-.002 0h825.144v253.659H-.002z"
                      />
                    </defs>
                    <clipPath id="trube-logo-header-2">
                      <use
                        xlinkHref="#trube-logo-header-1"
                        overflow="visible"
                      />
                    </clipPath>
                    <path
                      d="M253.657 126.83c0 70.046-56.783 126.829-126.829 126.829S-.002 196.876-.002 126.83C-.002 56.784 56.782.001 126.828.001S253.657 56.784 253.657 126.83"
                      clipPath="url(#trube-logo-header-2)"
                      fill="#ff7557"
                    />
                    <path
                      d="M152.841 76.326c.085 14.088-11.267 25.58-25.353 25.667-14.096.087-25.585-11.269-25.67-25.357-.085-14.093 11.267-25.584 25.36-25.667 14.087-.088 25.575 11.264 25.663 25.357M191.205 178.312s-85.609-51.471-122.115 5.27c0 0 51.524 46.32 122.115-5.27M140.581 109.402s-10.678 36.272-33.728 36.272c-20.339 0-39.828-43.728-45.256-44.067 0 0 11.87-14.408 27.292-4.409 28.582 18.529 40.332 16.952 51.692 12.204M131.963 144.569c39.928.598 90.674-48.001 36.537-67.407-5.506 37.671-32.277 61.322-36.537 67.407"
                      clipPath="url(#trube-logo-header-2)"
                      fill="#fff"
                    />
                    <path
                      id="trube-logo-header-text"
                      clipPath="url(#trube-logo-header-2)"
                      fill="currentColor"
                      d="M422.195 64.558H314.136v20.161h43.074v103.007h21.908V84.719h43.077zM504.768 144.092c0 8.493 1.269 17.373 3.236 21.93 1.971 4.581 4.874 8.636 8.621 12.051 3.695 3.385 8.238 6.098 13.497 8.08 5.233 1.954 13.592 2.95 20.173 2.95 6.583 0 14.821-.996 20.065-2.956 5.246-1.972 9.783-4.687 13.493-8.074 3.756-3.438 6.651-7.49 8.615-12.054 1.965-4.569 3.312-13.434 3.312-21.927v-39.427h-20.688l-.003 39.427c0 5.416-.562 11.517-1.614 14.358-1.069 2.897-2.631 5.45-4.69 7.669-2.055 2.241-4.662 4.039-7.74 5.369-6.194 2.713-15.264 2.688-21.458 0-3.081-1.33-5.689-3.128-7.768-5.369-2.035-2.219-3.555-4.815-4.663-7.669-1.104-2.842-1.607-8.942-1.607-14.358v-39.427h-20.854l.073 39.427zM693.424 117.675c6.634-5.664 8.265-13.762 7.996-21.777-.196-5.892-1.044-30.965-39.537-30.965H637.75v-.049h-21.905v123.785l53.782.024c8.636-.164 17.12-1.357 21.754-2.707 23.142-6.745 24.051-30.69 23.957-36.83-.109-6.887-3.056-25.78-21.914-31.481m-31.541-35.676c15.397.12 18.585 7.595 18.692 14.491.128 8.101-3.698 16.632-18.025 16.632h-24.8V81.996l24.133.003zm7.565 88.187H637.75v-40.269l31.698.002c15 0 23.73 7.865 23.73 19.291.001 12.821-6.501 20.6-23.73 20.976M437.008 116.194v-11.479h-20.739v83.535h20.739v-39.152c-.014-15.518 9.694-33.727 27.302-30.24 8.121 1.608 11.907 9.876 13.136 13.246l14.965-9.746c-1.261-2.222-2.289-4.327-3.526-6.265-11.2-17.565-38.678-17.063-51.877.101M808.361 151.579h16.336c.29-2.011.445-4.055.445-6.123 0-5.8-1.137-11.39-3.389-16.623-2.259-5.239-5.575-9.874-9.846-13.77-4.247-3.886-9.439-6.993-15.422-9.236-5.961-2.238-12.818-3.373-20.376-3.373-7.47 0-14.277 1.135-20.232 3.373-5.993 2.247-11.181 5.356-15.421 9.23-4.27 3.898-7.61 8.521-9.917 13.753-2.295 5.234-3.458 10.836-3.458 16.646 0 5.804 1.134 11.392 3.379 16.605 2.257 5.235 5.572 9.871 9.857 13.775 4.228 3.87 9.42 6.971 15.434 9.239 5.983 2.233 12.831 3.371 20.358 3.371 7.525 0 14.377-1.138 20.376-3.378 5.996-2.256 11.183-5.356 15.422-9.232a40.912 40.912 0 0 0 4.49-4.833l-14.226-11.047c-.833 1.209-2.868 3.579-4.265 4.827-2.6 2.313-5.99 4.286-9.514 5.807-7.083 3.099-17.45 3.069-24.529 0-3.521-1.521-6.503-3.578-8.88-6.141-2.326-2.535-4.112-5.484-5.332-8.766a27.585 27.585 0 0 1-1.192-4.105h59.902zm-59.523-13.93c.233-.862.506-1.707.815-2.532.05-.181.149-.33.198-.478 1.205-3.042 2.903-5.782 5.16-8.198 2.377-2.594 5.354-4.678 8.829-6.17 3.555-1.554 7.709-2.323 12.287-2.323 4.607 0 8.732.769 12.291 2.323 3.499 1.521 6.478 3.576 8.857 6.14a26.633 26.633 0 0 1 5.329 8.706c.311.829.582 1.674.816 2.532h-54.582z"
                    />
                  </svg>
                </a>
              </div>
            </div>
            <ul className="tb-nav-top-links tbk-ml-1 tbk-mb-0 tbk-flex tbk-min-w-0 tbk-grow tbk-items-center">
              <li
                className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${
                  showLinks ? " md:tbk-block" : ""
                }`}
              >
                <NavLink
                  to="/partners"
                  className={`tb-nav-link tbk-whitespace-nowrap ${
                    isNight ? "tbk-text-basic-white" : "tbk-text-primary"
                  }`}
                >
                  Find Your{" "}
                  <FormattedMessage id="Trainer" defaultMessage="Trainer" />
                </NavLink>
              </li>
              <li
                className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${
                  showLinks ? " md:tbk-block" : ""
                }`}
              >
                <NavLink
                  to="/lifestyle"
                  className={`tb-nav-link tbk-whitespace-nowrap ${
                    isNight ? "tbk-text-basic-white" : "tbk-text-primary"
                  }`}
                >
                  TruBe Lifestyle
                </NavLink>
              </li>
              <li
                className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${
                  showLinks ? " lg:tbk-block" : ""
                }`}
              >
                <NavLink
                  to="/about"
                  className={`tb-nav-link tbk-whitespace-nowrap ${
                    isNight ? "tbk-text-basic-white" : "tbk-text-primary"
                  }`}
                >
                  About Us
                </NavLink>
              </li>
              <li
                className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${
                  showLinks ? " lg:tbk-block" : ""
                }`}
              >
                <NavLink
                  to="/faq"
                  className={`tb-nav-link tbk-whitespace-nowrap ${
                    isNight ? "tbk-text-basic-white" : "tbk-text-grey-main"
                  }`}
                  // target="_blank"
                >
                  FAQ
                </NavLink>
              </li>
              {/*<li*/}
              {/*  className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${*/}
              {/*    showLinks ? " lg:tbk-block" : ""*/}
              {/*  }`}*/}
              {/*>*/}
              {/*  <NavLink*/}
              {/*    to="/become-a-member"*/}
              {/*    className={`tb-nav-link tbk-whitespace-nowrap ${*/}
              {/*      isNight ? "tbk-text-basic-white" : "tbk-text-primary"*/}
              {/*    }`}*/}
              {/*  >*/}
              {/*    Be a Member*/}
              {/*  </NavLink>*/}
              {/*</li>*/}
              {/*<li*/}
              {/*  className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${*/}
              {/*    showLinks ? " lg:tbk-block" : ""*/}
              {/*  }`}*/}
              {/*>*/}
              {/*  <NavLink*/}
              {/*    to="/become-a-coach"*/}
              {/*    className={`tb-nav-link tbk-whitespace-nowrap ${*/}
              {/*      isNight ? "tbk-text-basic-white" : "tbk-text-primary"*/}
              {/*    }`}*/}
              {/*  >*/}
              {/*    Be a Coach*/}
              {/*  </NavLink>*/}
              {/*</li>*/}
              {/*<li*/}
              {/*  className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${*/}
              {/*    showLinks ? " lg:tbk-block" : ""*/}
              {/*  }`}*/}
              {/*>*/}
              {/*  <NavLink*/}
              {/*    to="/pillars"*/}
              {/*    className={`tb-nav-link tbk-whitespace-nowrap ${*/}
              {/*      isNight ? "tbk-text-basic-white" : "tbk-text-primary"*/}
              {/*    }`}*/}
              {/*  >*/}
              {/*    Our Pillars*/}
              {/*  </NavLink>*/}
              {/*</li>*/}
              <li
                className={`tb-header-menu-item tbk-text-main tbk-mr-5 tbk-hidden${
                  showLinks ? " xl:tbk-block" : ""
                }`}
              >
                <NavLink
                  to="/contact"
                  className="tb-nav-link tbk-flex tbk-whitespace-nowrap tbk-text-primary"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="tbk-mr-1 tbk-hidden tbk-align-middle"
                  >
                    <path
                      d="M12.0023 3H11.9977C7.03537 3 3 7.0365 3 12C3 13.9688 3.6345 15.7935 4.71338 17.2751L3.59175 20.6186L7.05113 19.5128C8.47425 20.4555 10.1719 21 12.0023 21C16.9646 21 21 16.9624 21 12C21 7.03763 16.9646 3 12.0023 3ZM17.2391 15.7091C17.022 16.3222 16.1603 16.8307 15.4729 16.9792C15.0026 17.0794 14.3884 17.1593 12.3206 16.302C9.67575 15.2063 7.9725 12.5186 7.83975 12.3442C7.71263 12.1699 6.771 10.9211 6.771 9.62963C6.771 8.33813 7.42688 7.70925 7.69125 7.43925C7.90838 7.21763 8.26725 7.11637 8.6115 7.11637C8.72288 7.11637 8.823 7.122 8.913 7.1265C9.17737 7.13775 9.31012 7.1535 9.4845 7.57088C9.70162 8.094 10.2304 9.3855 10.2934 9.51825C10.3575 9.651 10.4216 9.831 10.3316 10.0054C10.2473 10.1854 10.173 10.2652 10.0402 10.4183C9.9075 10.5712 9.7815 10.6883 9.64875 10.8525C9.52725 10.9954 9.39 11.1484 9.543 11.4128C9.696 11.6715 10.2247 12.5344 11.0033 13.2274C12.0079 14.1217 12.8224 14.4075 13.1137 14.529C13.3309 14.619 13.5896 14.5976 13.7482 14.4289C13.9496 14.2118 14.1982 13.8518 14.4514 13.4974C14.6314 13.2431 14.8586 13.2116 15.0971 13.3016C15.3401 13.386 16.626 14.0216 16.8904 14.1532C17.1547 14.286 17.3291 14.349 17.3932 14.4604C17.4563 14.5718 17.4563 15.0949 17.2391 15.7091Z"
                      fill={isNight ? "#ffffff" : "#203b54"}
                    />
                  </svg>
                  Contact Us
                </NavLink>
              </li>
            </ul>
            {isLoggedIn || clientWidth() < 960
              ? showMenu && (
                  <Menu
                    isNight={isNight}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                  />
                )
              : showMenu && (
                  <div className="tbk-flex tbk-items-center">
                    <Link
                      to="/login/?authState=signUp"
                      className={`tbk-text-main tbk-mr-4 ${
                        isNight ? "tbk-text-basic-white" : "tbk-text-primary"
                      } hover:tbk-text-coral-light`}
                    >
                      Login
                    </Link>
                    <Link to={"/become-a-member"}>
                      <Button
                        variant={isNight ? "secondary" : null}
                        color={isNight ? "basic" : "coral"}
                        size="md"
                        startIcon={<UserPicOIcon />}
                      >
                        <div className="tbk-whitespace-nowrap tbk-pl-1">
                          Join Now
                        </div>
                      </Button>
                    </Link>
                  </div>
                )}
          </div>
        </div>
      </nav>
    </>
  );
};

Header.displayName = "Header";
Header.propTypes = {
  isVisible: PropTypes.bool,
  isNight: PropTypes.bool,
  showLinks: PropTypes.bool,
  showMenu: PropTypes.bool,
};
Header.defaultProps = {
  showLinks: true,
  showMenu: true,
};

export default Header;
